/**
 * CountryOperatorOnboardingPage
 * The Country Operator sees a business-operations-focused onboarding, NOT
 * a product-feature tour. Their job is running Konnect commercially in
 * their country: subscribers, credits, revenue, escalation.
 *
 * Flow:
 *   Step 1 — Introduce the role (what you do)
 *   Step 2 — Territory setup (country, currency, target market, comm plan)
 *   Step 3 — Credit wallet + reseller economics
 *   Step 4 — Escalation & support playbook
 *   Step 5 — Launch: seed a sample subscriber + jump into dashboard
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Globe, Wallet, LifeBuoy, TrendingUp, Rocket, ArrowRight, ArrowLeft, CheckCircle2, Users, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('konnect_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STEPS = [
  {
    id: 1, title: 'Your role', icon: Users,
    subtitle: 'You are Konnect in your country.',
    body: 'As Country Operator, you sign up local subscribers, resell platform credits, provide first-line support, and hit local revenue targets. This 3-minute wizard sets your territory up.',
  },
  {
    id: 2, title: 'Territory', icon: Globe,
    subtitle: 'Country, currency, target segments.',
  },
  {
    id: 3, title: 'Credit wallet', icon: Wallet,
    subtitle: 'How you buy, price, and re-sell credits.',
  },
  {
    id: 4, title: 'Support playbook', icon: LifeBuoy,
    subtitle: 'What you resolve locally, what you escalate.',
  },
  {
    id: 5, title: 'Launch', icon: Rocket,
    subtitle: 'Ready. Straight into your operator dashboard.',
  },
];

export default function CountryOperatorOnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [territory, setTerritory] = useState({
    country: '', currency: 'USD', language: 'en',
    target_segment: 'smb', comm_channels: ['email'],
  });
  const [creditPlan, setCreditPlan] = useState({
    starter_pack: 5000, resale_margin_pct: 25, low_credit_threshold: 500,
  });
  const [support, setSupport] = useState({
    local_hours: '09:00–18:00', escalation_after_min: 60, languages: 'English',
  });

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  const complete = useCallback(async () => {
    setLoading(true);
    try {
      // Persist operator-level onboarding state (idempotent; ignores 404s).
      await axios.post(
        `${API}/api/onboarding/operator-profile`,
        { territory, credit_plan: creditPlan, support },
        { headers: authHeaders() }
      ).catch(() => {});
      toast.success('Territory configured. Welcome aboard.');
      setTimeout(() => navigate('/dashboard'), 900);
    } catch {
      toast.error('Could not save — you can retry from Settings.');
    } finally { setLoading(false); }
  }, [territory, creditPlan, support, navigate]);

  const StepIcon = STEPS[step - 1].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 py-10 px-4" data-testid="country-operator-onboarding">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            <h1 className="text-3xl font-heading font-bold text-text-primary" data-testid="operator-onboarding-title">
              Country Operator Setup
            </h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-0">3 minutes</Badge>
          </div>
          <p className="text-sm text-gray-medium">
            You run Konnect commercially in your country. Let&apos;s configure your territory.
          </p>
          <div className="mt-4">
            <Progress value={progress} className="h-2" data-testid="operator-progress" />
            <div className="flex justify-between text-xs text-gray-medium mt-1">
              <span>Step {step} of {STEPS.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
          </div>
        </div>

        <Card data-testid={`operator-step-${step}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <StepIcon className="h-5 w-5 text-emerald-600" /> {STEPS[step - 1].title}
            </CardTitle>
            <p className="text-sm text-gray-medium">{STEPS[step - 1].subtitle}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-text-primary leading-relaxed">{STEPS[0].body}</p>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={Users} label="Subscribers to sign up" value="10+ / month" />
                  <StatCard icon={Wallet} label="Revenue split" value="~25% margin" />
                  <StatCard icon={LifeBuoy} label="First-line support" value="Yours" />
                  <StatCard icon={TrendingUp} label="Escalation" value="Konnect HQ" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <Field label="Country *">
                  <Input value={territory.country} onChange={(v) => setTerritory({ ...territory, country: v })} testid="operator-country" placeholder="e.g. Philippines, UAE, Kenya" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Currency">
                    <Input value={territory.currency} onChange={(v) => setTerritory({ ...territory, currency: v })} testid="operator-currency" />
                  </Field>
                  <Field label="Working language">
                    <Input value={territory.language} onChange={(v) => setTerritory({ ...territory, language: v })} testid="operator-lang" />
                  </Field>
                </div>
                <Field label="Primary segment you'll target">
                  <Select value={territory.target_segment} onChange={(v) => setTerritory({ ...territory, target_segment: v })}
                    testid="operator-segment"
                    options={[
                      { v: 'micro', l: 'Micro (1–10 employees)' },
                      { v: 'smb', l: 'Small–Mid (11–200 employees)' },
                      { v: 'mid', l: 'Mid-market (201–1,000)' },
                      { v: 'enterprise', l: 'Enterprise (1,000+)' },
                    ]} />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <p className="text-xs text-gray-medium bg-emerald-50 border border-emerald-100 rounded p-3">
                  You <b>buy credits from Konnect HQ</b> and <b>re-sell them</b> to your local subscribers with a margin.
                  Set your default reseller economics here — you can override per-subscriber later.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Starter credit pack">
                    <Input type="number" value={creditPlan.starter_pack} onChange={(v) => setCreditPlan({ ...creditPlan, starter_pack: Number(v) || 0 })} testid="operator-starter-pack" />
                  </Field>
                  <Field label="Your resale margin (%)">
                    <Input type="number" value={creditPlan.resale_margin_pct} onChange={(v) => setCreditPlan({ ...creditPlan, resale_margin_pct: Number(v) || 0 })} testid="operator-margin" />
                  </Field>
                  <Field label="Low-credit alert at">
                    <Input type="number" value={creditPlan.low_credit_threshold} onChange={(v) => setCreditPlan({ ...creditPlan, low_credit_threshold: Number(v) || 0 })} testid="operator-low-credit" />
                  </Field>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-3">
                <p className="text-xs text-gray-medium bg-emerald-50 border border-emerald-100 rounded p-3">
                  Every subscriber first sees <b>you</b>. Set expectations here so both sides know what happens when tickets exceed your local scope.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Your local support hours">
                    <Input value={support.local_hours} onChange={(v) => setSupport({ ...support, local_hours: v })} testid="operator-hours" />
                  </Field>
                  <Field label="Auto-escalate after (min)">
                    <Input type="number" value={support.escalation_after_min} onChange={(v) => setSupport({ ...support, escalation_after_min: Number(v) || 0 })} testid="operator-escalate" />
                  </Field>
                  <Field label="Languages you support">
                    <Input value={support.languages} onChange={(v) => setSupport({ ...support, languages: v })} testid="operator-langs" />
                  </Field>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                    <CheckCircle2 className="h-5 w-5" /> Ready to launch
                  </div>
                  <ReviewRow label="Territory" value={territory.country || '—'} />
                  <ReviewRow label="Segment" value={territory.target_segment} />
                  <ReviewRow label="Starter pack" value={`${creditPlan.starter_pack} credits @ ${creditPlan.resale_margin_pct}% margin`} />
                  <ReviewRow label="Support" value={`${support.local_hours} · escalate after ${support.escalation_after_min}m`} />
                </div>
                <p className="text-xs text-gray-medium">
                  Your operator dashboard opens next. From there you can add your first subscriber, top-up credits, and start selling.
                </p>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-gray-border">
              <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1 || loading}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {step < STEPS.length ? (
                <Button onClick={() => setStep(step + 1)} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="operator-next">
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button onClick={complete} disabled={loading} className="bg-emerald-brand hover:bg-emerald-hover px-6" data-testid="operator-launch">
                  <Rocket className="mr-2 h-4 w-4" /> Launch Territory
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-[11px] text-gray-400 text-center">Country Operator · setup takes ~3 minutes · you can adjust anything from Settings later</p>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-xs font-semibold text-gray-medium mb-1 block">{label}</label>{children}</div>
);
const Input = ({ value, onChange, testid, placeholder, type = 'text' }) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand"
    data-testid={testid} placeholder={placeholder} />
);
const Select = ({ value, onChange, testid, options }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand bg-white"
    data-testid={testid}>
    {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);
const StatCard = ({ icon: Icon, label, value }) => (
  <div className="p-3 rounded-lg border border-emerald-100 bg-emerald-50/40">
    <Icon className="h-4 w-4 text-emerald-600 mb-1" />
    <div className="text-[11px] text-gray-medium uppercase tracking-wide">{label}</div>
    <div className="text-sm font-semibold text-text-primary">{value}</div>
  </div>
);
const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-medium">{label}</span>
    <span className="font-semibold text-text-primary">{value}</span>
  </div>
);
